"use client";

import { useState, type ReactNode } from "react";
import { useTranslations } from "use-intl";

import { formatCatalogTaxonomyLabel } from "@/lib/music-taxonomy";
import { cn } from "@/lib/utils";
import type { RouterOutputs } from "@/types/rpc";

import { ArtistResultCard } from "./artist-result-card";

type Artist = RouterOutputs["music"]["searchArtists"]["artists"][number];

const NARROW_DESKTOP_ARTIST_COUNT = 3;
const WIDE_DESKTOP_ARTIST_COUNT = 5;

function ArtistsSection({ children }: { children: ReactNode }) {
  const t = useTranslations("globals.search");
  return (
    <section aria-label={t("artists")} className="mb-10">
      {children}
    </section>
  );
}

/**
 * Artist results section. Owns its own loading skeleton, inline error, and
 * empty-collapse so a slow or failing artist leg never blocks the albums.
 */
export function ArtistResults({
  artists,
  isLoading,
  error,
}: {
  artists: Artist[];
  isLoading: boolean;
  error?: string;
}) {
  const t = useTranslations("globals.search");
  const tCommon = useTranslations("globals.common");
  const tg = useTranslations("shared.genres");
  const ts = useTranslations("shared.styles");
  const [showAllArtists, setShowAllArtists] = useState(false);
  const formatStyleOrGenre = (value: string) => {
    const styleKey = value as Parameters<typeof ts>[0];
    if (ts.has(styleKey)) return ts(styleKey);

    const genreKey = value as Parameters<typeof tg>[0];
    if (tg.has(genreKey)) return tg(genreKey);

    return formatCatalogTaxonomyLabel(value);
  };

  if (error) {
    return (
      <ArtistsSection>
        <p className="text-sm text-muted-foreground py-2">{tCommon("error", { message: error })}</p>
      </ArtistsSection>
    );
  }

  if (isLoading && artists.length === 0) {
    return (
      <ArtistsSection>
        <div className="flex gap-8 overflow-hidden pb-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex flex-col items-center gap-3 w-28">
              <div className="size-24 rounded-full bg-secondary animate-pulse" />
              <div className="h-3 w-20 rounded bg-secondary animate-pulse" />
            </div>
          ))}
        </div>
      </ArtistsSection>
    );
  }

  if (artists.length === 0) return null;

  return (
    <ArtistsSection>
      <div className="flex gap-8 overflow-x-auto pb-4 no-scrollbar lg:flex-wrap">
        {artists.map((artist, index) => (
          <ArtistResultCard
            key={artist.id}
            artist={artist}
            index={index}
            showAllArtists={showAllArtists}
            formatStyleOrGenre={formatStyleOrGenre}
          />
        ))}
      </div>
      {artists.length > NARROW_DESKTOP_ARTIST_COUNT && (
        <button
          onClick={() => setShowAllArtists(!showAllArtists)}
          className={cn(
            "hidden text-sm font-medium text-primary hover:underline mt-2 lg:block",
            artists.length <= WIDE_DESKTOP_ARTIST_COUNT && "xl:hidden",
          )}
        >
          {showAllArtists ? t("showLessArtists") : t("showMoreArtists")}
        </button>
      )}
    </ArtistsSection>
  );
}
